import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Paper,
  Link,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';

const validationSchema = yup.object({
  login: yup
    .string()
    .required('Email или телефон обязательны'),
  password: yup
    .string()
    .required('Пароль обязателен')
});

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      login: '',
      password: ''
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleLogin(values);
    },
  });

  const handleLogin = async (values) => {
    try {
      setError('');
      const { login, password } = values;
      
      // Login
      const loginResponse = await authService.login({ login, password });

      // Опционально сохраняем credentials для автоматического восстановления сессии
      // (только для мобильного приложения или если пользователь хочет)
      const isMobileApp = window.isMobileApp || 
                          window.location.protocol === 'capacitor:' || 
                          window.location.protocol === 'ionic:';
      
      if (isMobileApp) {
        // Для мобильного приложения сохраняем credentials для авто-восстановления сессии
        localStorage.setItem('savedCredentials', JSON.stringify({ login, password }));
      }

      // Redirect based on user role
      let dashboardPath = '/respondent/dashboard'; // default
      console.log('Login response user:', loginResponse.user);
      console.log('User role:', loginResponse.user.role);
      
      if (loginResponse.user.role === 'admin') {
        dashboardPath = '/admin/dashboard';
      } else if (loginResponse.user.role === 'client') {
        dashboardPath = '/client/dashboard';
      } else if (loginResponse.user.role === 'respondent') {
        dashboardPath = '/respondent/dashboard';
      }

      console.log('Redirecting to:', dashboardPath);
      const redirectTo = location.state?.from || dashboardPath;
      
      // Send pending FCM token if mobile app
      console.log('[Login] isMobileApp:', isMobileApp);
      console.log('[Login] Protocol:', window.location.protocol);
      console.log('[Login] pendingFcmToken:', localStorage.getItem('pendingFcmToken'));
      
      if (isMobileApp) {
        console.log('[Login] Attempting to send pending FCM token...');
        import('../../mobile-init').then(({ sendPendingFcmToken }) => {
          sendPendingFcmToken();
        }).catch(err => {
          console.error('[Login] Failed to load mobile-init:', err);
        });
      } else {
        console.log('[Login] Not a mobile app, skipping FCM token send');
      }
      
      navigate(redirectTo);
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.status === 401) {
        setError('Неверный логин и/или пароль');
      } else if (error.response && error.response.status === 403) {
        setError('Вы были заблокированы администратором');
      } else {
        setError(error.response?.data?.error || 'Не удалось войти. Проверьте email и пароль.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 4 },
          mt: 8,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '24px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          component="h1" 
          variant="h4" 
          align="center" 
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#2d3748',
            mb: 3
          }}
        >
          Вход в систему
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.1), rgba(214, 48, 49, 0.1))',
                border: '1px solid rgba(214, 48, 49, 0.3)',
                '& .MuiAlert-icon': { color: '#d63031' },
                '& .MuiAlert-message': { color: '#d63031', fontWeight: 600 }
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            margin="normal"
            name="login"
            label="Email или телефон"
            value={formik.values.login}
            onChange={formik.handleChange}
            error={formik.touched.login && Boolean(formik.errors.login)}
            helperText={formik.touched.login && formik.errors.login}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused fieldset': {
                  borderColor: '#0984e3',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0984e3',
                fontWeight: 600
              }
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            name="password"
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&.Mui-focused fieldset': {
                  borderColor: '#0984e3',
                  borderWidth: 2
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#0984e3',
                fontWeight: 600
              }
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              mt: 3,
              mb: 2,
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              py: 1.5,
              fontSize: '1rem',
              boxShadow: '0 4px 20px rgba(9, 132, 227, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                boxShadow: '0 6px 25px rgba(9, 132, 227, 0.5)'
              }
            }}
          >
            Войти
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Link 
              component={RouterLink} 
              to="/forgot-password" 
              variant="body2"
              sx={{
                color: '#0984e3',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Забыли пароль?
            </Link>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              component={RouterLink}
              to="/register/respondent"
              variant="outlined"
              fullWidth
              sx={{
                color: '#0984e3',
                borderColor: '#0984e3',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                py: 1.2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#0984e3',
                  background: 'rgba(116, 185, 255, 0.1)'
                }
              }}
            >
              Регистрация
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}